import { ChangeDetectionStrategy, Component, HostListener, computed, effect, inject, signal, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { CeRequest, CeRequestStatus } from '../../models/ce-request.model';
import { CeRequestStateService } from '../../services/ce-request-state.service';
import {
  DictionariesApiService,
} from '../../services/dictionaries-api.service';
import { QuestionnaireApiService } from '../../services/questionnaire-api.service';
import {
  CostEstimatorDto,
  HistoricalProjectsFilterQuery,
  HistoricalProjectDto,
  ProjectsApiService,
  RequestedProjectDto,
  UpdateProjectCeCommand,
} from '../../services/projects-api.service';
import { CurrentUserService } from '../../services/current-user.service';
import { CreateQuestionCommand, QuestionnaireViewModel, UpdateQuestionCommand } from '../../models/questionnaire-api.model';

interface AdminDictionary {
  key: string;
  name: string;
  values: string[];
}

interface QuestionnaireAdminQuestion {
  id: number | null;
  description: string;
  isMandatory: boolean;
  hasAttachment: boolean;
  isMandatoryAttachment: boolean;
}

interface QuestionnaireAdminGroup {
  id: number | null;
  groupName: string | null;
  questions: QuestionnaireAdminQuestion[];
}

interface QuestionnaireAdminClass {
  classNumber: string;
  groups: QuestionnaireAdminGroup[];
}

interface FlattenedQuestionnaireQuestion extends QuestionnaireAdminQuestion {
  groupId: number | null;
}

const QUESTIONNAIRE_CLASS_NUMBERS = ['2', '3a', '3b', '4', '5'] as const;

const ADMIN_DICTIONARIES: AdminDictionary[] = [
  { key: 'estimate-request-type', name: 'Estimate Request Type', values: [] },
  { key: 'project-management-type', name: 'Project Management Type', values: [] },
  { key: 'cpdep-phase', name: 'CPDEP Phase', values: [] },
  { key: 'design-progress', name: 'Design Progress', values: [] },
  { key: 'area', name: 'Area', values: [] },
  { key: 'unit', name: 'Unit', values: [] },
  { key: 'ibp-year', name: 'IBP Year', values: [] },
  { key: 'ta-year', name: 'TA Year', values: [] },
  { key: 'equipment-name', name: 'Equipment name', values: [] },
  {
    key: 'contingency-calculation-method',
    name: 'Contingency calculation method',
    values: [],
  },
  { key: 'project-team', name: 'Project Team', values: [] },
  { key: 'alternative-number', name: 'Alternative No', values: [] },
  { key: 'type', name: 'Type', values: [] },
  { key: 'revision', name: 'Revision', values: [] },
  { key: 'questionnaire', name: 'Questionnaire', values: [...QUESTIONNAIRE_CLASS_NUMBERS] },
];

interface DictionaryValueRow {
  index: number;
  value: string;
}

interface DictionaryItemRecord {
  id: number | null;
  name: string;
}

type HistoricalSearchFieldKey =
  | 'estimateNumber'
  | 'projectTitle'
  | 'projectNumber'
  | 'projectCode'
  | 'estimateTitle'
  | 'afeNumber'
  | 'requestor'
  | 'tcoEstimator'
  | 'tcoSecondaryEstimator'
  | 'tcoReviewer'
  | 'comments';

interface HistoricalSearchFieldOption {
  key: HistoricalSearchFieldKey;
  label: string;
  paramName: string;
}

interface HistoricalSearchToken {
  key: HistoricalSearchFieldKey;
  label: string;
  value: string;
}

type HistoricalDateFilterKey =
  | 'projectCompletionDate'
  | 'dateReceived'
  | 'deadlineDate'
  | 'startDate'
  | 'dateCompleted';

interface DateRangeFilterValue {
  startDate: string;
  endDate: string;
}

const EMPTY_DATE_RANGE: DateRangeFilterValue = {
  startDate: '',
  endDate: '',
};

const HISTORICAL_DATE_FILTER_PARAM_BY_KEY: Record<HistoricalDateFilterKey, { start: string; end: string }> = {
  projectCompletionDate: {
    start: 'Filter.ProjectCompletionDate.StartDate',
    end: 'Filter.ProjectCompletionDate.EndDate',
  },
  dateReceived: {
    start: 'Filter.DateReceived.StartDate',
    end: 'Filter.DateReceived.EndDate',
  },
  deadlineDate: {
    start: 'Filter.DeadlineDate.StartDate',
    end: 'Filter.DeadlineDate.EndDate',
  },
  startDate: {
    start: 'Filter.StartDate.StartDate',
    end: 'Filter.StartDate.EndDate',
  },
  dateCompleted: {
    start: 'Filter.DateCompleted.StartDate',
    end: 'Filter.DateCompleted.EndDate',
  },
};

const HISTORICAL_SEARCH_FIELD_OPTIONS: readonly HistoricalSearchFieldOption[] = [
  { key: 'estimateNumber', label: 'Estimate Number', paramName: 'Filter.EstimateNumber.FilterValues' },
  { key: 'projectTitle', label: 'Project Title', paramName: 'Filter.ProjectTitle.FilterValues' },
  { key: 'projectNumber', label: 'Project Number', paramName: 'Filter.ProjectNumber.FilterValues' },
  { key: 'projectCode', label: 'Project Code', paramName: 'Filter.ProjectCode.FilterValues' },
  { key: 'estimateTitle', label: 'Estimate Title', paramName: 'Filter.EstimateTitle.FilterValues' },
  { key: 'afeNumber', label: 'AFE Number', paramName: 'Filter.AfeNumber.FilterValues' },
  { key: 'requestor', label: 'Requestor', paramName: 'Filter.Requestor.FilterValues' },
  { key: 'tcoEstimator', label: 'TCO Estimator', paramName: 'Filter.TcoEstimator.FilterValues' },
  { key: 'tcoSecondaryEstimator', label: 'TCO Secondary Estimator', paramName: 'Filter.TcoSecondaryEstimator.FilterValues' },
  { key: 'tcoReviewer', label: 'TCO Reviewer', paramName: 'Filter.TcoReviewer.FilterValues' },
  { key: 'comments', label: 'Comments', paramName: 'Filter.Comments.FilterValues' },
] as const;

type RequestsSearchFieldKey =
  | 'estimateNumber'
  | 'projectTitle'
  | 'requestor'
  | 'assignedCe';

interface RequestsSearchFieldOption {
  key: RequestsSearchFieldKey;
  label: string;
  paramName: string;
}

interface RequestsSearchToken {
  key: RequestsSearchFieldKey;
  label: string;
  value: string;
}

const REQUESTS_SEARCH_FIELD_OPTIONS: readonly RequestsSearchFieldOption[] = [
  { key: 'estimateNumber', label: 'Estimate Number', paramName: 'Filter.EstimateNumber.FilterValues' },
  { key: 'projectTitle', label: 'Project Title', paramName: 'Filter.ProjectTitle.FilterValues' },
  { key: 'requestor', label: 'Requestor', paramName: 'Filter.Requestor.FilterValues' },
  { key: 'assignedCe', label: 'Estimator', paramName: 'Filter.AssignedCe.FilterValues' },
] as const;

type HistoricalColumnFilterKey =
  | 'classOfEstimate'
  | 'turnaroundYear'
  | 'locationFacility'
  | 'locationUnit'
  | 'contingencyCalculationMethod'
  | 'agileMethodology'
  | 'status';

const HISTORICAL_STATUS_OPTIONS: readonly string[] = [
  'Draft',
  'Submitted',
  'Assigned CE',
  'Under Development',
  'Pending Additional Information',
  'Issued',
  'Canceled',
] as const;

const HISTORICAL_FILTER_PARAM_BY_KEY: Record<HistoricalColumnFilterKey, string> = {
  classOfEstimate: 'Filter.ClassOfEstimate.FilterValues',
  turnaroundYear: 'Filter.TurnaroundYear.FilterValues',
  locationFacility: 'Filter.LocationFacility.FilterValues',
  locationUnit: 'Filter.LocationUnit.FilterValues',
  contingencyCalculationMethod: 'Filter.ContingencyCalculationMethod.FilterValues',
  agileMethodology: 'Filter.AgileMethodology.FilterValues',
  status: 'Filter.ProjectStatusType.FilterValues',
};

const HISTORICAL_FILTER_ENDPOINT_BY_KEY: Partial<Record<HistoricalColumnFilterKey, string>> = {
  classOfEstimate: 'ClassOfEstimates',
  turnaroundYear: 'TurnaroundYears',
  locationFacility: 'Areas',
  locationUnit: 'Units',
  contingencyCalculationMethod: 'ContingencyCalculationMethods',
  agileMethodology: 'ProjectManagementTypes',
};

@Component({
  selector: 'app-my-ce-requests-assigned',
  imports: [DatePipe, FormsModule, NgbPaginationModule, MatIconModule, MatAutocompleteModule],
  templateUrl: './my-ce-requests-assigned.html',
  styleUrl: './my-ce-requests-assigned.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyCeRequestsAssigned {
  private readonly router = inject(Router);
  private readonly state = inject(CeRequestStateService);
  private readonly dictionariesApi = inject(DictionariesApiService);
  private readonly questionnaireApi = inject(QuestionnaireApiService);
  private readonly projectsApi = inject(ProjectsApiService);
  private readonly currentUserService = inject(CurrentUserService);
  private saveNoticeTimer: ReturnType<typeof setTimeout> | null = null;
  private isLoadingSettingsDictionaries = false;
  private isLoadingSettingsQuestionnaires = false;
  private isProjectsLoadingInFlight = false;
  private isHistoricalLoadingInFlight = false;
  private readonly dictionaryEndpoints: Record<string, string> = {
    'estimate-request-type': 'EstimateRequestTypes',
    'project-management-type': 'ProjectManagementTypes',
    'cpdep-phase': 'CpdepPhases',
    'design-progress': 'DesignProgresses',
    area: 'Areas',
    unit: 'Units',
    'ibp-year': 'IbpYears',
    'ta-year': 'TurnaroundYears',
    'equipment-name': 'Equipments',
    'contingency-calculation-method': 'ContingencyCalculationMethods',
    'project-team': 'ProjectTeams',
    'alternative-number': 'AlternativeNumbers',
    'type': 'Types',
    'revision': 'Revisions',
  };

  protected readonly user = this.currentUserService.profile;

  protected readonly costEstimators = signal<CostEstimatorDto[]>([]);
  protected readonly ceUsers = signal<string[]>([]);
  protected readonly defaultAssignee = computed(() => this.user().name || 'Unassigned');
  protected readonly assignedByRequest = signal<Record<string, string>>({});
  protected readonly previousAssignedByProject = signal<Record<string, string>>({});
  protected readonly estimatorEditModeByProject = signal<Record<string, boolean>>({});
  protected readonly estimatorSavingByProject = signal<Record<string, boolean>>({});
  protected readonly showAllAssigneeOptions = signal<Record<string, boolean>>({});
  protected readonly activeTab = signal<'historical' | 'requests' | 'settings'>('requests');
  protected readonly canManageSettings = computed(() =>
    this.currentUserService.hasAnyRole(['cet_admin']),
  );
  protected readonly selectedRequestStatusFilters = signal<CeRequestStatus[]>([]);
  protected readonly appliedRequestStatusFilters = signal<CeRequestStatus[]>([]);
  protected readonly dateFilterOpen = signal(false);
  protected readonly selectedStartDate = signal('');
  protected readonly selectedEndDate = signal('');
  protected readonly appliedStartDate = signal('');
  protected readonly appliedEndDate = signal('');
  protected readonly statuses: CeRequestStatus[] = [
    'Draft',
    'Submitted',
    'Assigned CE',
    'Under Development',
    'Pending Additional Information',
    'Issued',
    'Canceled',
  ];
  protected readonly statusDropdownOpen = signal(false);
  protected readonly dictionaries = signal<AdminDictionary[]>(
    this.cloneDictionaries(ADMIN_DICTIONARIES),
  );
  protected readonly savedDictionaries = signal<AdminDictionary[]>(
    this.cloneDictionaries(ADMIN_DICTIONARIES),
  );
  protected readonly dictionaryItemsByKey = signal<Record<string, DictionaryItemRecord[]>>(
    this.buildDictionaryItemMap(ADMIN_DICTIONARIES),
  );
  protected readonly savedDictionaryItemsByKey = signal<Record<string, DictionaryItemRecord[]>>(
    this.buildDictionaryItemMap(ADMIN_DICTIONARIES),
  );
  protected readonly selectedDictionaryKey = signal(ADMIN_DICTIONARIES[0]?.key ?? '');
  protected readonly selectedQuestionnaireClass = signal<string>(QUESTIONNAIRE_CLASS_NUMBERS[0] ?? '');
  protected readonly dictionarySearch = signal('');
  protected readonly newDictionaryValue = signal('');
  protected readonly dictionaryAddError = signal('');
  protected readonly dictionaryApiError = signal('');
  protected readonly dictionaryValueErrors = signal<Record<number, string>>({});
  protected readonly pendingRemoveDictionaryIndex = signal<number | null>(null);
  protected readonly showSaveSuccess = signal(false);

  protected readonly questionnaireByClass = signal<Record<string, QuestionnaireAdminClass>>(
    this.buildQuestionnaireMap(QUESTIONNAIRE_CLASS_NUMBERS),
  );
  protected readonly savedQuestionnaireByClass = signal<Record<string, QuestionnaireAdminClass>>(
    this.buildQuestionnaireMap(QUESTIONNAIRE_CLASS_NUMBERS),
  );
  protected readonly questionnaireAddDescription = signal('');
  protected readonly questionnaireAddDescriptionRequired = signal(false);
  protected readonly questionnaireAddGroupId = signal<number | null>(null);
  protected readonly questionnaireAddGroupRequired = signal(false);
  protected readonly questionnaireAddMandatory = signal(true);
  protected readonly questionnaireAddAttachment = signal(true);
  protected readonly questionnaireAddMandatoryAttachment = signal(false);
  protected readonly questionnaireApiError = signal('');
  protected readonly questionnaireValueErrors = signal<Record<string, string>>({});

  protected readonly projects = signal<RequestedProjectDto[]>([]);
  protected readonly projectsLoading = signal(false);
  protected readonly totalProjectRecords = signal(0);
  protected readonly historicalProjects = signal<HistoricalProjectDto[]>([]);
  protected readonly historicalLoading = signal(false);
  protected readonly historicalExporting = signal(false);
  protected readonly historicalFilterOpenKey = signal<HistoricalColumnFilterKey | null>(null);
  protected readonly historicalDateFilterOpenKey = signal<HistoricalDateFilterKey | null>(null);
  protected readonly historicalSelectedFilters = signal<Record<HistoricalColumnFilterKey, string[]>>({
    classOfEstimate: [],
    turnaroundYear: [],
    locationFacility: [],
    locationUnit: [],
    contingencyCalculationMethod: [],
    agileMethodology: [],
    status: [],
  });
  protected readonly historicalAppliedFilters = signal<Record<HistoricalColumnFilterKey, string[]>>({
    classOfEstimate: [],
    turnaroundYear: [],
    locationFacility: [],
    locationUnit: [],
    contingencyCalculationMethod: [],
    agileMethodology: [],
    status: [],
  });
  protected readonly historicalFilterOptions = signal<Record<HistoricalColumnFilterKey, string[]>>({
    classOfEstimate: [],
    turnaroundYear: [],
    locationFacility: [],
    locationUnit: [],
    contingencyCalculationMethod: [],
    agileMethodology: [],
    status: [...HISTORICAL_STATUS_OPTIONS],
  });
  protected readonly totalHistoricalRecords = signal(0);
  protected readonly historicalSelectedDateRanges = signal<Record<HistoricalDateFilterKey, DateRangeFilterValue>>({
    projectCompletionDate: { ...EMPTY_DATE_RANGE },
    dateReceived: { ...EMPTY_DATE_RANGE },
    deadlineDate: { ...EMPTY_DATE_RANGE },
    startDate: { ...EMPTY_DATE_RANGE },
    dateCompleted: { ...EMPTY_DATE_RANGE },
  });
  protected readonly historicalAppliedDateRanges = signal<Record<HistoricalDateFilterKey, DateRangeFilterValue>>({
    projectCompletionDate: { ...EMPTY_DATE_RANGE },
    dateReceived: { ...EMPTY_DATE_RANGE },
    deadlineDate: { ...EMPTY_DATE_RANGE },
    startDate: { ...EMPTY_DATE_RANGE },
    dateCompleted: { ...EMPTY_DATE_RANGE },
  });

  protected readonly pageSize = 10;
  protected readonly currentPageRequests = signal(1);
  protected readonly currentPageHistorical = signal(1);
  protected readonly search = signal('');
  protected readonly requestsSearchFieldOptions = REQUESTS_SEARCH_FIELD_OPTIONS;
  protected readonly requestsSearchTokens = signal<RequestsSearchToken[]>([]);
  protected readonly requestsAppliedSearchTokens = signal<RequestsSearchToken[]>([]);
  protected readonly requestsSearchFieldDropdownOpen = signal(false);
  protected readonly availableRequestsSearchFieldOptions = computed(() => {
    const selectedKeys = new Set(this.requestsSearchTokens().map(token => token.key));
    return this.requestsSearchFieldOptions.filter(option => !selectedKeys.has(option.key));
  });
  protected readonly historicalSearchFieldOptions = HISTORICAL_SEARCH_FIELD_OPTIONS;
  protected readonly historicalSearchTokens = signal<HistoricalSearchToken[]>([]);
  protected readonly historicalAppliedSearchTokens = signal<HistoricalSearchToken[]>([]);
  protected readonly historicalSearchFieldDropdownOpen = signal(false);
  protected readonly availableHistoricalSearchFieldOptions = computed(() => {
    const selectedKeys = new Set(this.historicalSearchTokens().map(token => token.key));
    return this.historicalSearchFieldOptions.filter(option => !selectedKeys.has(option.key));
  });

  constructor() {
    void this.currentUserService.load();
    void this.loadCostEstimators();

    effect(() => {
      const tab = this.activeTab();
      const pageReq = this.currentPageRequests();
      const pageHist = this.currentPageHistorical();
      
      if (tab === 'requests') {
        void untracked(() => this.loadProjects(pageReq));
      }
      if (tab === 'historical') {
        void untracked(() => this.loadHistoricalProjects(pageHist));
      }
    });
  }

  private async loadCostEstimators(): Promise<void> {
    try {
      const estimators = await firstValueFrom(this.projectsApi.getCostEstimators());
      const normalized = this.normalizeCostEstimators(estimators);
      const names = this.mapCostEstimatorNames(normalized);
      this.costEstimators.set(normalized);
      this.ceUsers.set(names);
    } catch (error) {
      console.error(error);
      this.costEstimators.set([]);
      this.ceUsers.set([]);
    }
  }

  private normalizeCostEstimators(estimators: readonly CostEstimatorDto[]): CostEstimatorDto[] {
    const deduped = new Map<string, CostEstimatorDto>();
    for (const estimator of estimators) {
      const objectId = estimator.objectId?.trim();
      const fullName = estimator.fullName?.trim();
      if (!objectId || !fullName) {
        continue;
      }
      deduped.set(objectId, { objectId, fullName });
    }

    return Array.from(deduped.values());
  }

  private mapCostEstimatorNames(estimators: readonly CostEstimatorDto[]): string[] {
    const names = estimators
      .map(estimator => estimator.fullName?.trim() ?? '')
      .filter((name): name is string => !!name);

    return Array.from(new Set(names));
  }

  protected openTab(tab: 'historical' | 'requests' | 'settings'): void {
    if (tab === 'settings' && !this.canManageSettings()) {
      return;
    }

    this.activeTab.set(tab);
    if (tab === 'settings') {
      void this.loadAllDictionariesFromApi();
      void this.loadAllQuestionnairesFromApi();
    }
  }

  protected onSearchInputChange(value: string): void {
    this.search.set(value);

    if (this.activeTab() === 'historical') {
      this.historicalSearchFieldDropdownOpen.set(!!value.trim());
      return;
    }

    if (this.activeTab() === 'requests') {
      this.requestsSearchFieldDropdownOpen.set(!!value.trim());
      this.currentPageRequests.set(1);
    }
  }

  protected onSearchInputFocus(): void {
    if (this.activeTab() === 'historical') {
      this.historicalSearchFieldDropdownOpen.set(!!this.search().trim());
      return;
    }

    if (this.activeTab() === 'requests') {
      this.requestsSearchFieldDropdownOpen.set(!!this.search().trim());
    }
  }

  protected addHistoricalSearchToken(fieldKey: HistoricalSearchFieldKey, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const value = this.search().trim();
    if (!value) {
      this.historicalSearchFieldDropdownOpen.set(false);
      return;
    }

    const option = this.historicalSearchFieldOptions.find(item => item.key === fieldKey);
    if (!option) {
      return;
    }

    const hasSameField = this.historicalSearchTokens().some(token => token.key === fieldKey);
    if (hasSameField) {
      return;
    }

    const exists = this.historicalSearchTokens().some(
      token => token.key === fieldKey && token.value.toLowerCase() === value.toLowerCase(),
    );
    if (!exists) {
      this.historicalSearchTokens.update(current => [
        ...current,
        { key: option.key, label: option.label, value },
      ]);
    }

    this.search.set('');
    this.historicalSearchFieldDropdownOpen.set(false);
  }

  protected removeHistoricalSearchToken(index: number): void {
    this.historicalSearchTokens.update(current => current.filter((_, idx) => idx !== index));
  }

  protected addRequestsSearchToken(fieldKey: RequestsSearchFieldKey, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const value = this.search().trim();
    if (!value) {
      this.requestsSearchFieldDropdownOpen.set(false);
      return;
    }

    const option = this.requestsSearchFieldOptions.find(item => item.key === fieldKey);
    if (!option) {
      return;
    }

    const hasSameField = this.requestsSearchTokens().some(token => token.key === fieldKey);
    if (hasSameField) {
      return;
    }

    const exists = this.requestsSearchTokens().some(
      token => token.key === fieldKey && token.value.toLowerCase() === value.toLowerCase(),
    );
    if (!exists) {
      this.requestsSearchTokens.update(current => [
        ...current,
        { key: option.key, label: option.label, value },
      ]);
    }

    this.search.set('');
    this.requestsSearchFieldDropdownOpen.set(false);
  }

  protected removeRequestsSearchToken(index: number): void {
    this.requestsSearchTokens.update(current => current.filter((_, idx) => idx !== index));
  }

  protected onSearchClick(): void {
    if (this.activeTab() === 'historical') {
      this.historicalSearchFieldDropdownOpen.set(false);
      this.historicalAppliedSearchTokens.set([...this.historicalSearchTokens()]);
      this.currentPageHistorical.set(1);
      void this.loadHistoricalProjects(1);
      return;
    }

    if (this.activeTab() === 'requests') {
      this.requestsSearchFieldDropdownOpen.set(false);
      this.requestsAppliedSearchTokens.set([...this.requestsSearchTokens()]);
      this.reloadRequestsFromFirstPage();
      return;
    }

    this.currentPageRequests.set(1);
  }

  protected toggleHistoricalFilter(key: HistoricalColumnFilterKey, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.historicalDateFilterOpenKey.set(null);
    if (this.historicalFilterOpenKey() === key) {
      this.historicalFilterOpenKey.set(null);
      return;
    }

    void this.loadHistoricalFilterOptions(key);
  }

  protected closeHistoricalFilter(): void {
    this.historicalFilterOpenKey.set(null);
  }

  protected toggleHistoricalDateFilter(key: HistoricalDateFilterKey, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.historicalFilterOpenKey.set(null);
    if (this.historicalDateFilterOpenKey() === key) {
      this.historicalDateFilterOpenKey.set(null);
      return;
    }
    this.historicalDateFilterOpenKey.set(key);
  }

  protected isHistoricalDateFilterOpen(key: HistoricalDateFilterKey): boolean {
    return this.historicalDateFilterOpenKey() === key;
  }

  protected updateHistoricalDateFilterValue(key: HistoricalDateFilterKey, field: 'startDate' | 'endDate', value: string): void {
    const normalized = (value ?? '').trim();
    this.historicalSelectedDateRanges.update(current => ({
      ...current,
      [key]: {
        ...(current[key] ?? EMPTY_DATE_RANGE),
        [field]: normalized,
      },
    }));
  }

  protected clearHistoricalDateFilter(key: HistoricalDateFilterKey): void {
    this.historicalSelectedDateRanges.update(current => ({
      ...current,
      [key]: { ...EMPTY_DATE_RANGE },
    }));
    this.historicalAppliedDateRanges.update(current => ({
      ...current,
      [key]: { ...EMPTY_DATE_RANGE },
    }));
    this.historicalDateFilterOpenKey.set(null);
    this.currentPageHistorical.set(1);
    void this.loadHistoricalProjects(1);
  }

  protected applyHistoricalDateFilter(key: HistoricalDateFilterKey): void {
    const selectedRange = this.historicalSelectedDateRanges()[key] ?? EMPTY_DATE_RANGE;
    this.historicalAppliedDateRanges.update(current => ({
      ...current,
      [key]: {
        startDate: selectedRange.startDate,
        endDate: selectedRange.endDate,
      },
    }));
    this.historicalDateFilterOpenKey.set(null);
    this.currentPageHistorical.set(1);
    void this.loadHistoricalProjects(1);
  }

  protected isHistoricalFilterOpen(key: HistoricalColumnFilterKey): boolean {
    return this.historicalFilterOpenKey() === key;
  }

  protected isHistoricalColumnFilterApplied(key: HistoricalColumnFilterKey): boolean {
    return (this.historicalAppliedFilters()[key] ?? []).length > 0;
  }

  protected isHistoricalDateFilterApplied(key: HistoricalDateFilterKey): boolean {
    const range = this.historicalAppliedDateRanges()[key] ?? EMPTY_DATE_RANGE;
    return !!range.startDate || !!range.endDate;
  }

  protected getHistoricalFilterOptions(key: HistoricalColumnFilterKey): readonly string[] {
    return this.historicalFilterOptions()[key] ?? [];
  }

  protected isHistoricalFilterValueSelected(key: HistoricalColumnFilterKey, value: string): boolean {
    return (this.historicalSelectedFilters()[key] ?? []).includes(value);
  }

  protected toggleHistoricalFilterValue(key: HistoricalColumnFilterKey, value: string): void {
    this.historicalSelectedFilters.update(current => {
      const selected = current[key] ?? [];
      const next = selected.includes(value)
        ? selected.filter(item => item !== value)
        : [...selected, value];

      return {
        ...current,
        [key]: next,
      };
    });
  }

  protected clearHistoricalFilterValues(key: HistoricalColumnFilterKey): void {
    this.historicalSelectedFilters.update(current => ({
      ...current,
      [key]: [],
    }));
    this.historicalAppliedFilters.update(current => ({
      ...current,
      [key]: [],
    }));
    this.historicalFilterOpenKey.set(null);
    this.currentPageHistorical.set(1);
    void this.loadHistoricalProjects(1);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const openKey = this.historicalFilterOpenKey();
    const openDateKey = this.historicalDateFilterOpenKey();
    const isSearchDropdownOpen = this.historicalSearchFieldDropdownOpen();
    const isRequestsSearchDropdownOpen = this.requestsSearchFieldDropdownOpen();
    if (!openKey && !openDateKey && !isSearchDropdownOpen && !isRequestsSearchDropdownOpen && !this.statusDropdownOpen() && !this.dateFilterOpen()) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      this.historicalFilterOpenKey.set(null);
      this.historicalDateFilterOpenKey.set(null);
      this.historicalSearchFieldDropdownOpen.set(false);
      this.requestsSearchFieldDropdownOpen.set(false);
      this.statusDropdownOpen.set(false);
      this.dateFilterOpen.set(false);
      return;
    }

    if (
      target.closest('.column-filter-dropdown') ||
      target.closest('.column-filter-trigger') ||
      target.closest('.date-filter-dropdown') ||
      target.closest('.date-filter-trigger') ||
      target.closest('.requests-date-filter-dropdown') ||
      target.closest('.requests-date-filter-trigger') ||
      target.closest('.status-dropdown') ||
      target.closest('.status-header-wrapper') ||
      target.closest('.historical-search-field-dropdown') ||
      target.closest('.requests-search-field-dropdown') ||
      target.closest('.search-input')
    ) {
      return;
    }

    this.historicalFilterOpenKey.set(null);
    this.historicalDateFilterOpenKey.set(null);
    this.historicalSearchFieldDropdownOpen.set(false);
    this.requestsSearchFieldDropdownOpen.set(false);
    this.statusDropdownOpen.set(false);
    this.dateFilterOpen.set(false);
  }

  protected applyHistoricalFilters(): void {
    this.historicalFilterOpenKey.set(null);
    this.historicalSearchFieldDropdownOpen.set(false);
    this.historicalAppliedFilters.set(this.cloneHistoricalFilters(this.historicalSelectedFilters()));
    this.historicalAppliedSearchTokens.set([...this.historicalSearchTokens()]);
    this.currentPageHistorical.set(1);
    void this.loadHistoricalProjects(1);
  }

  protected async exportHistoricalToExcel(): Promise<void> {
    if (this.historicalExporting()) {
      return;
    }

    this.historicalExporting.set(true);
    try {
      const response = await firstValueFrom(this.projectsApi.exportHistoricalProjects());
      const fileName = this.getExportFileName(response.headers.get('content-disposition'));
      const fileBlob = response.body;
      if (!fileBlob) {
        return;
      }

      const objectUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error(error);
      // Ignore export errors silently for now to keep current UX behavior.
    } finally {
      this.historicalExporting.set(false);
    }
  }

  private getExportFileName(contentDisposition: string | null): string {
    if (!contentDisposition) {
      return 'HistoricalProjects.xlsx';
    }

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      try {
        return decodeURIComponent(utfMatch[1]);
      } catch (error) {
        console.error(error);
        return utfMatch[1];
      }
    }

    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return asciiMatch?.[1] || 'HistoricalProjects.xlsx';
  }

  private async loadProjects(page: number = 1): Promise<void> {
    if (this.isProjectsLoadingInFlight) return;
    this.isProjectsLoadingInFlight = true;
    this.projectsLoading.set(true);
    try {
      const response = await firstValueFrom(
        this.projectsApi.getRequestedProjects(page, this.pageSize, this.buildRequestedProjectsFilterQuery()),
      );
      this.projects.set(response.items);
      this.totalProjectRecords.set(response.totalRecords);
    } catch (error) {
      console.error(error);
      this.projects.set([]);
      this.totalProjectRecords.set(0);
    } finally {
      this.projectsLoading.set(false);
      this.isProjectsLoadingInFlight = false;
    }
  }

  private async loadHistoricalProjects(page: number = 1): Promise<void> {
    if (this.isHistoricalLoadingInFlight) return;
    this.isHistoricalLoadingInFlight = true;
    this.historicalLoading.set(true);
    try {
      const response = await firstValueFrom(
        this.projectsApi.getHistoricalProjects(page, this.pageSize, this.buildHistoricalFilterQuery()),
      );
      this.historicalProjects.set(response.items);
      this.totalHistoricalRecords.set(response.totalRecords);
    } catch (error) {
      console.error('Error loading historical projects:', error);
      this.historicalProjects.set([]);
      this.totalHistoricalRecords.set(0);
    } finally {
      this.historicalLoading.set(false);
      this.isHistoricalLoadingInFlight = false;
    }
  }

  protected readonly selectedDictionary = computed(() =>
    this.dictionaries().find(dictionary => dictionary.key === this.selectedDictionaryKey()) ?? null,
  );

  protected readonly questionnaireClasses = QUESTIONNAIRE_CLASS_NUMBERS;

  protected readonly selectedQuestionnaire = computed<QuestionnaireAdminClass | null>(() =>
    this.questionnaireByClass()[this.selectedQuestionnaireClass()] ?? null,
  );

  protected readonly questionnaireGroups = computed(() => this.selectedQuestionnaire()?.groups ?? []);

  protected readonly questionnaireAddGroupOptions = computed(() =>
    this.questionnaireGroups().filter(group => group.id !== null),
  );

  protected readonly filteredDictionaryValues = computed<DictionaryValueRow[]>(() => {
    const dictionary = this.selectedDictionary();
    if (!dictionary) {
      return [];
    }

    const query = this.dictionarySearch().trim().toLowerCase();
    return dictionary.values
      .map((value, index) => ({ index, value }))
      .filter(({ value }) => !query || value.toLowerCase().includes(query));
  });

  protected readonly hasDictionaryChanges = computed(() => {
    if (this.selectedDictionaryKey() === 'questionnaire') {
      return this.hasQuestionnaireChanges();
    }

    const key = this.selectedDictionaryKey();
    const current = this.dictionaries().find(dictionary => dictionary.key === key);
    const saved = this.savedDictionaries().find(dictionary => dictionary.key === key);

    if (!current || !saved) {
      return false;
    }

    if (current.values.length !== saved.values.length) {
      return true;
    }

    return current.values.some((value, index) => value !== saved.values[index]);
  });

  protected readonly hasDictionaryValidationErrors = computed(
    () => Object.keys(this.dictionaryValueErrors()).length > 0 || Object.keys(this.questionnaireValueErrors()).length > 0,
  );

  private readonly readonlyDictionaryKeys = new Set([
    'project-management-type',
    'cpdep-phase',
    'design-progress',
  ]);

  protected isReadonlyDictionary(key: string): boolean {
    return this.readonlyDictionaryKeys.has(key);
  }

  protected readonly filteredProjects = computed(() => {
    return this.projects();
  });

  protected readonly filteredProjectsTotal = computed(() => {
    return this.totalProjectRecords();
  });

  protected readonly filteredRequests = computed(() => {
    return this.historicalProjects();
  });

  protected readonly filteredTotal = computed(() => {
    return this.totalHistoricalRecords();
  });

  protected statusClass(status: CeRequestStatus | string | null | undefined): string {
    if (!status) return 'status';
    return 'status status--' + status.toLowerCase().replace(/\s+/g, '-');
  }

  protected toggleStatusDropdown(): void {
    this.dateFilterOpen.set(false);
    this.statusDropdownOpen.update(v => !v);
  }

  protected isRequestedStatusFilterSelected(status: CeRequestStatus): boolean {
    return this.selectedRequestStatusFilters().includes(status);
  }

  protected hasAppliedRequestedStatusFilters(): boolean {
    return this.appliedRequestStatusFilters().length > 0;
  }

  protected toggleRequestedStatusFilterValue(status: CeRequestStatus): void {
    this.selectedRequestStatusFilters.update(current => (
      current.includes(status)
        ? current.filter(item => item !== status)
        : [...current, status]
    ));
  }

  protected clearRequestedStatusFilters(): void {
    this.selectedRequestStatusFilters.set([]);
    this.appliedRequestStatusFilters.set([]);
    this.reloadRequestsFromFirstPage();
    this.statusDropdownOpen.set(false);
  }

  protected applyRequestedStatusFilters(): void {
    this.appliedRequestStatusFilters.set([...this.selectedRequestStatusFilters()]);
    this.reloadRequestsFromFirstPage();
    this.statusDropdownOpen.set(false);
  }

  protected toggleRequestsDateFilter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.statusDropdownOpen.set(false);
    this.dateFilterOpen.update(v => !v);
  }

  protected hasAppliedRequestedDateFilters(): boolean {
    return !!this.appliedStartDate().trim() || !!this.appliedEndDate().trim();
  }

  protected clearRequestedDateFilters(): void {
    this.selectedStartDate.set('');
    this.selectedEndDate.set('');
    this.appliedStartDate.set('');
    this.appliedEndDate.set('');
    this.reloadRequestsFromFirstPage();
    this.dateFilterOpen.set(false);
  }

  protected applyRequestedDateFilters(): void {
    this.appliedStartDate.set(this.selectedStartDate().trim());
    this.appliedEndDate.set(this.selectedEndDate().trim());
    this.reloadRequestsFromFirstPage();
    this.dateFilterOpen.set(false);
  }

  private reloadRequestsFromFirstPage(): void {
    if (this.currentPageRequests() !== 1) {
      this.currentPageRequests.set(1);
      return;
    }

    void this.loadProjects(1);
  }

  private buildRequestedProjectsFilterQuery(): HistoricalProjectsFilterQuery {
    const query: HistoricalProjectsFilterQuery = {};

    for (const token of this.requestsAppliedSearchTokens()) {
      const option = this.requestsSearchFieldOptions.find(item => item.key === token.key);
      if (!option) {
        continue;
      }

      const existing = query[option.paramName] ?? [];
      query[option.paramName] = [...existing, token.value];
    }

    const statusTypes = this.appliedRequestStatusFilters()
      .map(status => this.mapStatusLabelToType(status))
      .filter((value): value is number => value !== null);

    if (statusTypes.length) {
      query['Filter.ProjectStatusType.FilterValues'] = statusTypes;
    }

    const startDate = this.appliedStartDate().trim();
    if (startDate) {
      query['Filter.SubmittedDate.StartDate'] = [startDate];
    }

    const endDate = this.appliedEndDate().trim();
    if (endDate) {
      query['Filter.SubmittedDate.EndDate'] = [endDate];
    }

    return query;
  }

  private buildHistoricalFilterQuery(): HistoricalProjectsFilterQuery {
    const selected = this.historicalAppliedFilters();
    const query: HistoricalProjectsFilterQuery = {};

    for (const [key, paramName] of Object.entries(HISTORICAL_FILTER_PARAM_BY_KEY) as [HistoricalColumnFilterKey, string][]) {
      const values = selected[key] ?? [];
      if (!values.length) {
        continue;
      }

      if (key === 'status') {
        const statusValues = values
          .map(value => this.mapStatusLabelToType(value))
          .filter((value): value is number => value !== null);
        if (statusValues.length) {
          query[paramName] = statusValues;
        }
        continue;
      }

      query[paramName] = values;
    }

    for (const token of this.historicalAppliedSearchTokens()) {
      const option = this.historicalSearchFieldOptions.find(item => item.key === token.key);
      if (!option) {
        continue;
      }

      const existing = query[option.paramName] ?? [];
      query[option.paramName] = [...existing, token.value];
    }

    const dateRanges = this.historicalAppliedDateRanges();
    for (const [key, params] of Object.entries(HISTORICAL_DATE_FILTER_PARAM_BY_KEY) as [HistoricalDateFilterKey, { start: string; end: string }][]) {
      const range = dateRanges[key] ?? EMPTY_DATE_RANGE;
      if (range.startDate) {
        query[params.start] = [range.startDate];
      }
      if (range.endDate) {
        query[params.end] = [range.endDate];
      }
    }

    return query;
  }

  private cloneHistoricalFilters(
    filters: Record<HistoricalColumnFilterKey, string[]>,
  ): Record<HistoricalColumnFilterKey, string[]> {
    return {
      classOfEstimate: [...(filters.classOfEstimate ?? [])],
      turnaroundYear: [...(filters.turnaroundYear ?? [])],
      locationFacility: [...(filters.locationFacility ?? [])],
      locationUnit: [...(filters.locationUnit ?? [])],
      contingencyCalculationMethod: [...(filters.contingencyCalculationMethod ?? [])],
      agileMethodology: [...(filters.agileMethodology ?? [])],
      status: [...(filters.status ?? [])],
    };
  }

  private mapStatusLabelToType(status: string): number | null {
    switch (status) {
      case 'Draft': return 0;
      case 'Submitted': return 1;
      case 'Assigned CE': return 2;
      case 'Under Development': return 3;
      case 'Issued': return 4;
      case 'Pending Additional Information': return 5;
      case 'Canceled': return 6;
      default: return null;
    }
  }

  private async loadHistoricalFilterOptions(key: HistoricalColumnFilterKey): Promise<void> {
    const existing = this.historicalFilterOptions()[key] ?? [];
    if (existing.length) {
      this.historicalFilterOpenKey.set(key);
      return;
    }

    const endpoint = HISTORICAL_FILTER_ENDPOINT_BY_KEY[key];

    if (!endpoint) {
      this.historicalFilterOptions.update(current => ({
        ...current,
        [key]: this.extractHistoricalFilterValuesFromRows(key),
      }));
      this.historicalFilterOpenKey.set(key);
      return;
    }

    try {
      const items = await firstValueFrom(this.dictionariesApi.getDictionaryItems(endpoint));
      const values = items.map(item => item.name).filter(Boolean);
      this.historicalFilterOptions.update(current => ({
        ...current,
        [key]: values,
      }));
    } catch (error) {
      console.error(error);
      this.historicalFilterOptions.update(current => ({
        ...current,
        [key]: this.extractHistoricalFilterValuesFromRows(key),
      }));
    }

    this.historicalFilterOpenKey.set(key);
  }

  private extractHistoricalFilterValuesFromRows(key: HistoricalColumnFilterKey): string[] {
    const rows = this.historicalProjects();
    const valuesByKey: Record<HistoricalColumnFilterKey, string[]> = {
      classOfEstimate: rows.map(row => row.classOfEstimate ?? ''),
      turnaroundYear: rows.map(row => row.turnaroundYear ?? ''),
      locationFacility: rows.map(row => row.locationFacility ?? ''),
      locationUnit: rows.map(row => row.locationUnit ?? ''),
      contingencyCalculationMethod: rows.map(row => row.contingencyCalculationMethod ?? ''),
      agileMethodology: rows.map(row => row.agileMethodology ?? ''),
      status: rows.map(row => this.historicalStatusLabel(row)),
    };

    return Array.from(new Set(valuesByKey[key].map(value => value.trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }

  protected createRequest(): void {
    this.state.resetDraft();
    this.router.navigate(['/project-team']);
  }

  protected view(request: HistoricalProjectDto): void {
    this.router.navigate(['/response-page'], { queryParams: { id: request.estimateNumber } });
  }

  protected viewProject(project: RequestedProjectDto): void {
    this.router.navigate(['/response-page'], { queryParams: { projectId: project.id } });
  }

  protected formatEstimateNumber(project: RequestedProjectDto): string {
    return project.estimateNumber ?? '';
  }

  protected getRequestedProjectDate(project: RequestedProjectDto): string | null {
    return project.ceRequestDate ?? project.createdDate ?? project.submittedDate ?? null;
  }

  protected projectStatusLabel(statusType: number): CeRequestStatus {
    switch (statusType) {
      case 0: return 'Draft';
      case 1: return 'Submitted';
      case 2: return 'Assigned CE';
      case 3: return 'Under Development';
      case 4: return 'Issued';
      case 5: return 'Pending Additional Information';
      case 6: return 'Canceled';
      default: return 'Draft';
    }
  }

  protected historicalStatusLabel(project: HistoricalProjectDto): CeRequestStatus {
    const rawStatus = (project.status ?? '').trim().toLowerCase();

    if (rawStatus === 'draft') return 'Draft';
    if (rawStatus === 'submitted') return 'Submitted';
    if (rawStatus === 'assigned ce') return 'Assigned CE';
    if (rawStatus === 'under development') return 'Under Development';
    if (rawStatus === 'pending additional information') return 'Pending Additional Information';
    if (rawStatus === 'issued') return 'Issued';
    if (rawStatus === 'canceled' || rawStatus === 'cancelled') return 'Canceled';

    // Fallback to status type mapping when API returns null/unknown status text.
    return this.projectStatusLabel(project.projectStatusType);
  }

  protected getProjectAssignedUser(project: RequestedProjectDto): string {
    const projectKey = String(project.id);
    const assignedOverrides = this.assignedByRequest();
    if (Object.prototype.hasOwnProperty.call(assignedOverrides, projectKey)) {
      return assignedOverrides[projectKey] ?? '';
    }

    const assignedFromApi = project.assignedCe?.trim();
    if (assignedFromApi) {
      return assignedFromApi;
    }

    return '';
  }

  protected openProjectAssigneeOptions(project: RequestedProjectDto, trigger: MatAutocompleteTrigger): void {
    if (!this.isProjectEstimatorEditing(project)) {
      return;
    }

    this.showAllAssigneeOptions.update(current => ({ ...current, [String(project.id)]: true }));
    trigger.openPanel();
  }

  protected closeProjectAssigneeOptions(project: RequestedProjectDto): void {
    setTimeout(() => {
      this.showAllAssigneeOptions.update(current => ({ ...current, [String(project.id)]: false }));
    }, 120);
  }

  protected getFilteredCeUsersForProject(project: RequestedProjectDto): readonly string[] {
    const users = this.ceUsers();
    const query = this.getProjectAssignedUser(project).trim().toLowerCase();

    if (this.showAllAssigneeOptions()[String(project.id)] && !query) {
      return users;
    }

    if (!query) {
      return users;
    }

    return users.filter(user => user.toLowerCase().includes(query));
  }

  protected isProjectEstimatorEditing(project: RequestedProjectDto): boolean {
    return !!this.estimatorEditModeByProject()[String(project.id)];
  }

  protected isProjectEstimatorSaving(project: RequestedProjectDto): boolean {
    return !!this.estimatorSavingByProject()[String(project.id)];
  }

  protected canConfirmProjectEstimator(project: RequestedProjectDto): boolean {
    if (!this.isProjectEstimatorEditing(project) || this.isProjectEstimatorSaving(project)) {
      return false;
    }

    const selectedName = this.getProjectAssignedUser(project).trim();
    if (!selectedName) {
      return false;
    }

    return !!this.findCostEstimatorByName(selectedName);
  }

  protected getProjectEstimatorActionLabel(project: RequestedProjectDto): string {
    if (this.isProjectEstimatorSaving(project)) {
      return 'Saving...';
    }

    return this.isProjectEstimatorEditing(project) ? 'Confirm' : 'Change';
  }

  protected isEstimatorChangeLocked(project: RequestedProjectDto): boolean {
    const status = this.projectStatusLabel(project.projectStatusType);
    return status === 'Issued' || status === 'Canceled';
  }

  protected isProjectEstimatorActionDisabled(project: RequestedProjectDto): boolean {
    if (this.isEstimatorChangeLocked(project)) {
      return true;
    }

    if (this.isProjectEstimatorSaving(project)) {
      return true;
    }

    if (this.isProjectEstimatorEditing(project)) {
      return !this.canConfirmProjectEstimator(project);
    }

    return false;
  }

  protected onProjectEstimatorAction(
    project: RequestedProjectDto,
    trigger: MatAutocompleteTrigger,
    input: HTMLInputElement,
  ): void {
    if (this.isProjectEstimatorEditing(project)) {
      void this.confirmProjectEstimator(project);
      return;
    }

    this.beginProjectEstimatorChange(project, trigger, input);
  }

  protected beginProjectEstimatorChange(
    project: RequestedProjectDto,
    trigger: MatAutocompleteTrigger,
    input: HTMLInputElement,
  ): void {
    if (this.isProjectEstimatorSaving(project)) {
      return;
    }

    const projectKey = String(project.id);
    const previousValue = this.getProjectAssignedUser(project);

    this.previousAssignedByProject.update(current => ({ ...current, [projectKey]: previousValue }));
    this.assignedByRequest.update(current => ({ ...current, [projectKey]: '' }));
    this.estimatorEditModeByProject.update(current => ({ ...current, [projectKey]: true }));
    this.showAllAssigneeOptions.update(current => ({ ...current, [projectKey]: true }));

    requestAnimationFrame(() => {
      input.focus();
      trigger.openPanel();
    });
  }

  protected cancelProjectEstimatorChange(project: RequestedProjectDto, trigger: MatAutocompleteTrigger): void {
    if (!this.isProjectEstimatorEditing(project) || this.isProjectEstimatorSaving(project)) {
      return;
    }

    const projectKey = String(project.id);
    const previousValue = this.previousAssignedByProject()[projectKey] ?? '';

    this.assignedByRequest.update(current => ({ ...current, [projectKey]: previousValue }));
    this.estimatorEditModeByProject.update(current => ({ ...current, [projectKey]: false }));
    this.showAllAssigneeOptions.update(current => ({ ...current, [projectKey]: false }));
    trigger.closePanel();
  }

  protected async confirmProjectEstimator(project: RequestedProjectDto): Promise<void> {
    if (!this.canConfirmProjectEstimator(project)) {
      return;
    }

    const selectedName = this.getProjectAssignedUser(project).trim();
    const selectedEstimator = this.findCostEstimatorByName(selectedName);
    if (!selectedEstimator) {
      return;
    }

    this.estimatorSavingByProject.update(current => ({ ...current, [String(project.id)]: true }));
    const command: UpdateProjectCeCommand = {
      id: project.id,
      ceEngineerObjectId: selectedEstimator.objectId,
      tcoEstimator: selectedEstimator.fullName,
    };

    try {
      await firstValueFrom(this.projectsApi.updateProjectCe(project.id, command));

      // Update project status to "Assigned CE" (projectStatusType = 2) — non-critical
      try {
        const currentResponse = await firstValueFrom(this.projectsApi.getProjectResponse(project.id));
        await firstValueFrom(this.projectsApi.updateProjectResponse(project.id, {
          id: project.id,
          tcoEstimator: selectedEstimator.fullName,
          tcoSecondaryEstimator: currentResponse.tcoSecondaryEstimator ?? '',
          tcoReviewer: currentResponse.tcoReviewer ?? '',
          projectStatusType: 2,
          dateCompleted: currentResponse.dateCompleted ?? null,
          alternativeNumber: currentResponse.alternativeNumber ?? '',
          type: currentResponse.type ?? '',
          revision: currentResponse.revision ?? '',
          contingencyCalculationMethod: currentResponse.contingencyCalculationMethod ?? '',
          comments: '',
        }));
      } catch (error) {
        console.error(error);
        // Status update failure is non-critical; CE assignment already succeeded.
      }

      this.projects.update(items =>
        items.map(item =>
          item.id === project.id
            ? { ...item, assignedCe: selectedEstimator.fullName, projectStatusType: 2 }
            : item,
        ),
      );

      this.assignedByRequest.update(current => ({
        ...current,
        [String(project.id)]: selectedEstimator.fullName,
      }));
      this.estimatorEditModeByProject.update(current => ({ ...current, [String(project.id)]: false }));
      this.showAllAssigneeOptions.update(current => ({ ...current, [String(project.id)]: false }));
    } catch (error) {
      console.error(error);
      // Keep edit mode open to let user retry selection/confirm.
    } finally {
      this.estimatorSavingByProject.update(current => ({ ...current, [String(project.id)]: false }));
    }
  }

  private findCostEstimatorByName(fullName: string): CostEstimatorDto | null {
    const target = fullName.trim().toLowerCase();
    if (!target) {
      return null;
    }

    return (
      this.costEstimators().find(
        estimator => estimator.fullName.trim().toLowerCase() === target,
      ) ?? null
    );
  }

  protected setProjectAssignedUser(project: RequestedProjectDto, assignee: string, input?: HTMLInputElement): void {
    this.assignedByRequest.update(current => ({ ...current, [String(project.id)]: assignee }));
    if (input && this.ceUsers().includes(assignee)) {
      input.blur();
    }
  }

  protected getAssignedUser(request: CeRequest): string {
    return this.assignedByRequest()[request.estimateNumber] ?? this.defaultAssignee();
  }

  protected openAssigneeOptions(request: CeRequest, trigger: MatAutocompleteTrigger): void {
    this.showAllAssigneeOptions.update(current => ({
      ...current,
      [request.estimateNumber]: true,
    }));
    trigger.openPanel();
  }

  protected closeAssigneeOptions(request: CeRequest): void {
    setTimeout(() => {
      this.showAllAssigneeOptions.update(current => ({
        ...current,
        [request.estimateNumber]: false,
      }));
    }, 120);
  }

  protected getFilteredCeUsers(request: CeRequest): readonly string[] {
    const users = this.ceUsers();
    if (this.showAllAssigneeOptions()[request.estimateNumber]) {
      return users;
    }

    const query = this.getAssignedUser(request).trim().toLowerCase();
    if (!query) {
      return users;
    }

    return users.filter(user => user.toLowerCase().includes(query));
  }

  protected setAssignedUser(request: CeRequest, assignee: string, input?: HTMLInputElement): void {
    this.assignedByRequest.update(current => ({
      ...current,
      [request.estimateNumber]: assignee,
    }));

    if (input && this.ceUsers().includes(assignee)) {
      input.blur();
    }
  }

  protected selectDictionary(key: string): void {
    this.selectedDictionaryKey.set(key);
    this.dictionarySearch.set('');
    this.newDictionaryValue.set('');
    this.dictionaryAddError.set('');
    this.dictionaryApiError.set('');
    this.dictionaryValueErrors.set({});
    this.pendingRemoveDictionaryIndex.set(null);
    this.questionnaireAddDescription.set('');
    this.questionnaireAddDescriptionRequired.set(false);
    this.questionnaireAddGroupId.set(null);
    this.questionnaireAddGroupRequired.set(false);
    this.questionnaireAddMandatory.set(true);
    this.questionnaireAddAttachment.set(true);
    this.questionnaireAddMandatoryAttachment.set(false);
    this.questionnaireApiError.set('');
    this.questionnaireValueErrors.set({});

    if (key === 'questionnaire' && !this.selectedQuestionnaireClass()) {
      this.selectedQuestionnaireClass.set(QUESTIONNAIRE_CLASS_NUMBERS[0] ?? '');
    }
  }

  protected selectQuestionnaireClass(classNumber: string): void {
    this.selectedQuestionnaireClass.set(classNumber);
    this.questionnaireAddDescription.set('');
    this.questionnaireAddDescriptionRequired.set(false);
    this.questionnaireAddGroupId.set(null);
    this.questionnaireAddGroupRequired.set(false);
    this.questionnaireAddMandatory.set(true);
    this.questionnaireAddAttachment.set(true);
    this.questionnaireAddMandatoryAttachment.set(false);
    this.questionnaireApiError.set('');
    this.questionnaireValueErrors.set({});
  }

  protected onQuestionnaireAddGroupChange(value: number | '' | null): void {
    const normalizedGroupId = value === null || value === '' ? null : Number(value);
    this.questionnaireAddGroupId.set(Number.isFinite(normalizedGroupId as number) ? normalizedGroupId : null);

    if (this.questionnaireAddGroupId() !== null) {
      this.questionnaireAddGroupRequired.set(false);
    }
  }

  protected onQuestionnaireAddDescriptionChange(value: string): void {
    this.questionnaireAddDescription.set(value);

    if (value.trim()) {
      this.questionnaireAddDescriptionRequired.set(false);
    }
  }

  protected onNewDictionaryValueChange(value: string): void {
    this.newDictionaryValue.set(value);
    if (this.dictionaryAddError()) {
      this.dictionaryAddError.set('');
    }
  }

  protected updateDictionaryValue(index: number, value: string): void {
    this.dictionaryApiError.set('');
    const key = this.selectedDictionaryKey();
    const items = [...this.getCurrentDictionaryItems(key)];
    if (!items[index]) {
      return;
    }

    items[index] = { ...items[index], name: value };
    this.dictionaryValueErrors.set(this.getDictionaryDuplicateErrors(items.map(item => item.name)));
    this.setCurrentDictionaryItems(key, items);
  }

  protected askRemoveDictionaryValue(index: number): void {
    this.pendingRemoveDictionaryIndex.set(index);
  }

  protected cancelRemoveDictionaryValue(): void {
    this.pendingRemoveDictionaryIndex.set(null);
  }

  protected confirmRemoveDictionaryValue(): void {
    const index = this.pendingRemoveDictionaryIndex();
    if (index === null) {
      return;
    }

    this.pendingRemoveDictionaryIndex.set(null);
    this.removeDictionaryValue(index);
  }

  protected getPendingRemoveDictionaryValue(): string {
    const index = this.pendingRemoveDictionaryIndex();
    if (index === null) {
      return '';
    }

    const dictionary = this.selectedDictionary();
    return dictionary?.values[index] ?? '';
  }

  private removeDictionaryValue(index: number): void {
    this.dictionaryApiError.set('');
    const key = this.selectedDictionaryKey();
    const items = this.getCurrentDictionaryItems(key).filter((_, valueIndex) => valueIndex !== index);
    this.dictionaryValueErrors.set(this.getDictionaryDuplicateErrors(items.map(item => item.name)));
    this.setCurrentDictionaryItems(key, items);
  }

  protected addDictionaryValue(): void {
    const candidate = this.newDictionaryValue().trim();
    if (!candidate) {
      this.dictionaryAddError.set('Enter a value before adding.');
      return;
    }

    const selectedDictionary = this.selectedDictionary();
    if (!selectedDictionary) {
      return;
    }

    const normalizedCandidate = candidate.toLocaleLowerCase();
    const hasDuplicate = selectedDictionary.values.some(
      value => value.trim().toLocaleLowerCase() === normalizedCandidate,
    );
    if (hasDuplicate) {
      this.dictionaryAddError.set('This value already exists in the dictionary.');
      return;
    }

    this.dictionaryAddError.set('');
    this.dictionaryApiError.set('');

    const key = this.selectedDictionaryKey();
    const items = [...this.getCurrentDictionaryItems(key), { id: null, name: candidate }];
    this.setCurrentDictionaryItems(key, items);
    this.dictionaryValueErrors.set(this.getDictionaryDuplicateErrors(items.map(item => item.name)));

    this.newDictionaryValue.set('');
  }

  protected addQuestionnaireQuestion(): void {
    const description = this.questionnaireAddDescription().trim();
    if (!description) {
      this.questionnaireAddDescriptionRequired.set(true);
      this.questionnaireApiError.set('Enter a question before adding.');
      return;
    }

    this.questionnaireAddDescriptionRequired.set(false);

    const targetGroupId = this.questionnaireAddGroupId();
    if (targetGroupId === null) {
      this.questionnaireAddGroupRequired.set(true);
      this.questionnaireApiError.set('Select a target group before adding a question.');
      return;
    }

    const classNumber = this.selectedQuestionnaireClass();
    const selected = this.questionnaireByClass()[classNumber];
    if (!selected) {
      return;
    }

    const targetGroupExists = selected.groups.some(group => group.id === targetGroupId);
    if (!targetGroupExists) {
      this.questionnaireApiError.set('Selected group is unavailable. Refresh questionnaire and try again.');
      return;
    }

    const nextGroups = selected.groups.length
      ? selected.groups.map(group => (
          group.id === targetGroupId
            ? {
                ...group,
                questions: [
                  ...group.questions,
                  {
                    id: null,
                    description,
                    isMandatory: this.questionnaireAddMandatory(),
                    hasAttachment: this.questionnaireAddAttachment(),
                    isMandatoryAttachment: this.questionnaireAddMandatoryAttachment(),
                  },
                ],
              }
            : group
        ))
      : [{
          id: null,
          groupName: null,
          questions: [
            {
              id: null,
              description,
              isMandatory: this.questionnaireAddMandatory(),
              hasAttachment: this.questionnaireAddAttachment(),
              isMandatoryAttachment: this.questionnaireAddMandatoryAttachment(),
            },
          ],
        }];

    this.questionnaireByClass.update(current => ({
      ...current,
      [classNumber]: { classNumber, groups: nextGroups },
    }));
    this.questionnaireValueErrors.set(this.getQuestionnaireDuplicateErrors(nextGroups));
    this.questionnaireAddDescription.set('');
    this.questionnaireAddDescriptionRequired.set(false);
    this.questionnaireAddGroupId.set(null);
    this.questionnaireAddGroupRequired.set(false);
    this.questionnaireAddMandatory.set(true);
    this.questionnaireAddAttachment.set(true);
    this.questionnaireAddMandatoryAttachment.set(false);
    this.questionnaireApiError.set('');
  }

  protected updateQuestionnaireQuestion(
    groupIndex: number,
    questionIndex: number,
    field: 'description' | 'isMandatory' | 'hasAttachment' | 'isMandatoryAttachment',
    value: string | boolean,
  ): void {
    const classNumber = this.selectedQuestionnaireClass();
    const selected = this.questionnaireByClass()[classNumber];
    if (!selected) {
      return;
    }

    const nextGroups = selected.groups.map((group, currentGroupIndex) => {
      if (currentGroupIndex !== groupIndex) {
        return group;
      }

      return {
        ...group,
        questions: group.questions.map((question, currentQuestionIndex) => {
          if (currentQuestionIndex !== questionIndex) {
            return question;
          }

          return {
            ...question,
            [field]: value,
          } as QuestionnaireAdminQuestion;
        }),
      };
    });

    this.questionnaireByClass.update(current => ({
      ...current,
      [classNumber]: { classNumber, groups: nextGroups },
    }));
    this.questionnaireValueErrors.set(this.getQuestionnaireDuplicateErrors(nextGroups));
    this.questionnaireApiError.set('');
  }

  protected removeQuestionnaireQuestion(groupIndex: number, questionIndex: number): void {
    const classNumber = this.selectedQuestionnaireClass();
    const selected = this.questionnaireByClass()[classNumber];
    if (!selected) {
      return;
    }

    const nextGroups = selected.groups
      .map((group, currentGroupIndex) => {
        if (currentGroupIndex !== groupIndex) {
          return group;
        }

        return {
          ...group,
          questions: group.questions.filter((_, currentQuestionIndex) => currentQuestionIndex !== questionIndex),
        };
      })
      .filter(group => group.questions.length > 0);

    this.questionnaireByClass.update(current => ({
      ...current,
      [classNumber]: { classNumber, groups: nextGroups },
    }));
    this.questionnaireValueErrors.set(this.getQuestionnaireDuplicateErrors(nextGroups));
    this.questionnaireApiError.set('');
  }

  protected async saveDictionary(): Promise<void> {
    if (this.selectedDictionaryKey() === 'questionnaire') {
      await this.saveQuestionnaire();
      return;
    }

    const key = this.selectedDictionaryKey();
    const current = this.dictionaries().find(dictionary => dictionary.key === key);
    if (!current) {
      return;
    }

    if (this.hasDictionaryValidationErrors()) {
      this.dictionaryApiError.set('Fix validation errors before saving.');
      return;
    }

    this.dictionaryApiError.set('');
    const endpoint = this.dictionaryEndpoints[key];

    // Fallback to local-only save when endpoint is not configured.
    if (!endpoint) {
      this.savedDictionaries.update(saved =>
        saved.map(dictionary =>
          dictionary.key === key
            ? {
                ...dictionary,
                values: [...current.values],
              }
            : dictionary,
        ),
      );
      this.showSavedNotice();
      return;
    }

    try {
      const currentItems = this.getCurrentDictionaryItems(key);
      const savedItems = this.getSavedDictionaryItems(key);

      const currentById = new Map<number, DictionaryItemRecord>();
      for (const item of currentItems) {
        if (item.id !== null) {
          currentById.set(item.id, item);
        }
      }

      for (const item of savedItems) {
        if (item.id !== null && !currentById.has(item.id)) {
          await firstValueFrom(this.dictionariesApi.deleteDictionaryItem(endpoint, item.id));
        }
      }

      const savedById = new Map<number, DictionaryItemRecord>();
      for (const item of savedItems) {
        if (item.id !== null) {
          savedById.set(item.id, item);
        }
      }

      for (const item of currentItems) {
        if (item.id === null) {
          await firstValueFrom(this.dictionariesApi.createDictionaryItem(endpoint, item.name.trim()));
          continue;
        }

        const previous = savedById.get(item.id);
        if (!previous) {
          continue;
        }

        if (previous.name.trim() !== item.name.trim()) {
          await firstValueFrom(
            this.dictionariesApi.updateDictionaryItem(endpoint, item.id, item.name.trim()),
          );
        }
      }

      await this.loadDictionaryFromApi(key);
      this.dictionaryValueErrors.set({});
      this.showSavedNotice();
    } catch (error) {
      console.error(error);
      this.dictionaryApiError.set('Failed to save dictionary changes to backend.');
    }
  }

  private async saveQuestionnaire(): Promise<void> {
    const classNumber = this.selectedQuestionnaireClass();
    const current = this.questionnaireByClass()[classNumber];
    const saved = this.savedQuestionnaireByClass()[classNumber];

    if (!current || !saved) {
      return;
    }

    if (Object.keys(this.questionnaireValueErrors()).length > 0) {
      this.questionnaireApiError.set('Fix validation errors before saving.');
      return;
    }

    const currentQuestions = this.flattenQuestionnaireGroups(current.groups);
    const savedQuestions = this.flattenQuestionnaireGroups(saved.groups);
    const savedById = new Map<number, FlattenedQuestionnaireQuestion>();
    for (const question of savedQuestions) {
      if (question.id !== null) {
        savedById.set(question.id, question);
      }
    }

    const currentById = new Map<number, FlattenedQuestionnaireQuestion>();
    for (const question of currentQuestions) {
      if (question.id !== null) {
        currentById.set(question.id, question);
      }
    }

    this.questionnaireApiError.set('');

    try {
      for (const question of savedQuestions) {
        if (question.id !== null && !currentById.has(question.id)) {
          await firstValueFrom(this.questionnaireApi.deleteQuestion(question.id));
        }
      }

      for (const question of currentQuestions) {
        if (!question.description.trim()) {
          continue;
        }

        if (question.id === null) {
          if (question.groupId === null) {
            this.questionnaireApiError.set('Failed to resolve question group for creation.');
            return;
          }

          const createdId = await firstValueFrom(
            this.questionnaireApi.createQuestion({
              questionnaireId: question.groupId,
              description: question.description.trim(),
              isMandatory: question.isMandatory,
              hasAttachment: question.hasAttachment,
              isMandatoryAttachment: question.isMandatoryAttachment,
            }),
          );
          question.id = createdId;
          continue;
        }

        const previous = savedById.get(question.id);
        if (
          previous &&
          (
            previous.description.trim() !== question.description.trim() ||
            previous.isMandatory !== question.isMandatory ||
            previous.hasAttachment !== question.hasAttachment ||
            previous.isMandatoryAttachment !== question.isMandatoryAttachment
          )
        ) {
          const command: UpdateQuestionCommand = {
            id: question.id,
            description: question.description.trim(),
            isMandatory: question.isMandatory,
            hasAttachment: question.hasAttachment,
            isMandatoryAttachment: question.isMandatoryAttachment,
          };
          await firstValueFrom(this.questionnaireApi.updateQuestion(command));
        }
      }

      await this.loadQuestionnaireFromApi(classNumber);
      this.showSavedNotice();
    } catch (error) {
      console.error(error);
      this.questionnaireApiError.set('Failed to save questionnaire changes to backend.');
    }
  }

  private showSavedNotice(): void {
    this.showSaveSuccess.set(true);
    if (this.saveNoticeTimer) {
      clearTimeout(this.saveNoticeTimer);
    }
    this.saveNoticeTimer = setTimeout(() => {
      this.showSaveSuccess.set(false);
      this.saveNoticeTimer = null;
    }, 2500);
  }

  private async loadAllDictionariesFromApi(): Promise<void> {
    if (this.isLoadingSettingsDictionaries) {
      return;
    }

    this.isLoadingSettingsDictionaries = true;
    const keys = this.dictionaries().map(dictionary => dictionary.key);
    try {
      await Promise.all(keys.map(key => this.loadDictionaryFromApi(key)));
    } finally {
      this.isLoadingSettingsDictionaries = false;
    }
  }

  private async loadAllQuestionnairesFromApi(): Promise<void> {
    if (this.isLoadingSettingsQuestionnaires) {
      return;
    }

    this.isLoadingSettingsQuestionnaires = true;
    try {
      await Promise.all(QUESTIONNAIRE_CLASS_NUMBERS.map(classNumber => this.loadQuestionnaireFromApi(classNumber)));
    } finally {
      this.isLoadingSettingsQuestionnaires = false;
    }
  }

  private async loadQuestionnaireFromApi(classNumber: string): Promise<void> {
    try {
      const questionnaire = await firstValueFrom(this.questionnaireApi.getQuestionnaire(classNumber));
      const groups = this.mapQuestionnaireGroups(questionnaire);
      const questionnaireClass: QuestionnaireAdminClass = { classNumber, groups };

      this.questionnaireByClass.update(current => ({
        ...current,
        [classNumber]: this.cloneQuestionnaireClass(questionnaireClass),
      }));
      this.savedQuestionnaireByClass.update(current => ({
        ...current,
        [classNumber]: this.cloneQuestionnaireClass(questionnaireClass),
      }));
    } catch (error) {
      console.error(error);
      // Keep current in-memory values when backend is unavailable.
    }
  }

  private async loadDictionaryFromApi(key: string): Promise<void> {
    const endpoint = this.dictionaryEndpoints[key];
    if (!endpoint) {
      return;
    }

    try {
      const items = await firstValueFrom(this.dictionariesApi.getDictionaryItems(endpoint));
      const records = items.map(item => ({ id: item.id, name: item.name }));
      this.setCurrentDictionaryItems(key, records);
      this.setSavedDictionaryItems(key, records);
    } catch (error) {
      console.error(error);
      // Keep fallback values from local seed data when API is unavailable.
    }
  }

  private buildDictionaryItemMap(dictionaries: readonly AdminDictionary[]): Record<string, DictionaryItemRecord[]> {
    const map: Record<string, DictionaryItemRecord[]> = {};
    dictionaries.forEach(dictionary => {
      map[dictionary.key] = dictionary.values.map(value => ({ id: null, name: value }));
    });
    return map;
  }

  private buildQuestionnaireMap(classNumbers: readonly string[]): Record<string, QuestionnaireAdminClass> {
    const map: Record<string, QuestionnaireAdminClass> = {};
    classNumbers.forEach(classNumber => {
      map[classNumber] = { classNumber, groups: [] };
    });
    return map;
  }

  private mapQuestionnaireGroups(questionnaire: QuestionnaireViewModel): QuestionnaireAdminGroup[] {
    return (questionnaire.questionGroups ?? []).map(group => ({
      id: group.id ?? null,
      groupName: group.groupName ?? null,
      questions: (group.questions ?? []).map(question => ({
        id: question.id,
        description: question.description ?? '',
        isMandatory: question.isMandatory,
        hasAttachment: question.hasAttachment,
        isMandatoryAttachment: question.isMandatoryAttachment ?? false,
      })),
    }));
  }

  private cloneQuestionnaireClass(source: QuestionnaireAdminClass): QuestionnaireAdminClass {
    return {
      classNumber: source.classNumber,
      groups: source.groups.map(group => ({
        id: group.id,
        groupName: group.groupName,
        questions: group.questions.map(question => ({ ...question })),
      })),
    };
  }

  private flattenQuestionnaireGroups(groups: readonly QuestionnaireAdminGroup[]): FlattenedQuestionnaireQuestion[] {
    return groups.flatMap(group =>
      group.questions.map(question => ({
        ...question,
        groupId: group.id,
      })),
    );
  }

  private getQuestionnaireDuplicateErrors(groups: readonly QuestionnaireAdminGroup[]): Record<string, string> {
    const errors: Record<string, string> = {};
    const seen = new Map<string, string[]>();

    groups.forEach((group, groupIndex) => {
      group.questions.forEach((question, questionIndex) => {
        const normalized = question.description.trim().toLocaleLowerCase();
        if (!normalized) {
          errors[`${groupIndex}:${questionIndex}`] = 'Question text is required.';
          return;
        }

        const indices = seen.get(normalized) ?? [];
        indices.push(`${groupIndex}:${questionIndex}`);
        seen.set(normalized, indices);
      });
    });

    seen.forEach(indices => {
      if (indices.length < 2) {
        return;
      }

      indices.forEach(index => {
        errors[index] = 'Duplicate question text. Please enter a unique value.';
      });
    });

    return errors;
  }

  private hasQuestionnaireChanges(): boolean {
    const classNumber = this.selectedQuestionnaireClass();
    const current = this.questionnaireByClass()[classNumber];
    const saved = this.savedQuestionnaireByClass()[classNumber];

    if (!current || !saved) {
      return false;
    }

    const currentQuestions = this.flattenQuestionnaireGroups(current.groups);
    const savedQuestions = this.flattenQuestionnaireGroups(saved.groups);

    if (currentQuestions.length !== savedQuestions.length) {
      return true;
    }

    return currentQuestions.some((question, index) => {
      const previous = savedQuestions[index];
      return !previous ||
        previous.description.trim() !== question.description.trim() ||
        previous.isMandatory !== question.isMandatory ||
        previous.hasAttachment !== question.hasAttachment ||
        previous.isMandatoryAttachment !== question.isMandatoryAttachment;
    });
  }

  private getCurrentDictionaryItems(key: string): DictionaryItemRecord[] {
    return this.dictionaryItemsByKey()[key] ?? [];
  }

  private getSavedDictionaryItems(key: string): DictionaryItemRecord[] {
    return this.savedDictionaryItemsByKey()[key] ?? [];
  }

  private setCurrentDictionaryItems(key: string, items: DictionaryItemRecord[]): void {
    this.dictionaryItemsByKey.update(current => ({
      ...current,
      [key]: items.map(item => ({ ...item })),
    }));

    this.dictionaries.update(current =>
      current.map(dictionary =>
        dictionary.key === key
          ? {
              ...dictionary,
              values: items.map(item => item.name),
            }
          : dictionary,
      ),
    );
  }

  private setSavedDictionaryItems(key: string, items: DictionaryItemRecord[]): void {
    this.savedDictionaryItemsByKey.update(current => ({
      ...current,
      [key]: items.map(item => ({ ...item })),
    }));

    this.savedDictionaries.update(saved =>
      saved.map(dictionary =>
        dictionary.key === key
          ? {
              ...dictionary,
              values: items.map(item => item.name),
            }
          : dictionary,
      ),
    );
  }

  private cloneDictionaries(dictionaries: readonly AdminDictionary[]): AdminDictionary[] {
    return dictionaries.map(dictionary => ({
      ...dictionary,
      values: [...dictionary.values],
    }));
  }

  private getDictionaryDuplicateErrors(values: readonly string[]): Record<number, string> {
    const groups = new Map<string, number[]>();

    values.forEach((value, index) => {
      const normalized = value.trim().toLocaleLowerCase();
      if (!normalized) {
        return;
      }

      const indices = groups.get(normalized) ?? [];
      indices.push(index);
      groups.set(normalized, indices);
    });

    const errors: Record<number, string> = {};
    groups.forEach(indices => {
      if (indices.length < 2) {
        return;
      }

      indices.forEach(index => {
        errors[index] = 'Duplicate value. Please enter a unique value.';
      });
    });

    return errors;
  }
}

